import React, { Component } from 'react';

const MAX_LOGOS = 5;

const styles = {
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
    },
    sectionLabel: {
        fontSize: '11px',
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: '8px',
    },
    uploadBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        width: '100%',
        padding: '9px 0',
        background: '#1a1a1a',
        border: '2px dashed #444',
        borderRadius: '6px',
        color: '#aaa',
        fontSize: '12px',
        cursor: 'pointer',
        transition: 'border-color 0.15s, color 0.15s',
        boxSizing: 'border-box',
    },
    uploadBtnDisabled: {
        opacity: 0.35,
        cursor: 'not-allowed',
    },
    logoCard: {
        background: '#1a1a1a',
        border: '1px solid #2a2a2a',
        borderRadius: '6px',
        padding: '10px 10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    logoCardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    logoThumb: {
        width: '36px',
        height: '36px',
        objectFit: 'contain',
        borderRadius: '3px',
        background: '#111',
        flexShrink: 0,
        border: '1px solid #333',
    },
    logoName: {
        flex: 1,
        fontSize: '11px',
        color: '#ccc',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    removeBtn: {
        all: 'unset',
        color: '#555',
        cursor: 'pointer',
        fontSize: '14px',
        lineHeight: 1,
        padding: '2px 4px',
        borderRadius: '3px',
        transition: 'color 0.12s',
        flexShrink: 0,
    },
    controlRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
    },
    controlLabel: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '10px',
        color: '#666',
    },
    controlValue: {
        color: '#999',
    },
    slider: {
        width: '100%',
        cursor: 'pointer',
        accentColor: '#ecff19',
    },
    divider: {
        borderTop: '1px solid #2a2a2a',
    },
    hint: {
        fontSize: '10px',
        color: '#444',
        lineHeight: 1.5,
    },
};

/**
 * LogoPlacer
 *
 * Props:
 *   logos        – Logo[]  { id, name, dataUrl, x, y, scale, rotate }
 *   onAdd        – (logoObj) => void
 *   onRemove     – (id) => void
 *   onUpdate     – (id, patch) => void   patch is a partial Logo object
 */
class LogoPlacer extends Component {
    fileInputRef = React.createRef();

    handleFileChange = (e) => {
        const { logos, onAdd } = this.props;
        const files = Array.from(e.target.files);
        // Reset input so same file can be re-selected
        e.target.value = '';

        files.slice(0, MAX_LOGOS - logos.length).forEach((file) => {
            const reader = new FileReader();
            reader.onload = (ev) => {
                onAdd({
                    id: `logo-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                    name: file.name,
                    dataUrl: ev.target.result,
                    // Default: centered in the SVG canvas (1650×1550)
                    x: 825,
                    y: 775,
                    scale: 1,
                    rotate: 0,
                });
            };
            reader.readAsDataURL(file);
        });
    };

    render() {
        const { logos, onRemove, onUpdate } = this.props;
        const canAdd = logos.length < MAX_LOGOS;

        return (
            <div style={styles.root}>

                {/* ── Upload button ── */}
                <div>
                    <div style={styles.sectionLabel}>Logos ({logos.length} / {MAX_LOGOS})</div>
                    <button
                        style={{
                            ...styles.uploadBtn,
                            ...(canAdd ? {} : styles.uploadBtnDisabled),
                        }}
                        onClick={() => canAdd && this.fileInputRef.current.click()}
                        disabled={!canAdd}
                        title={canAdd ? 'Upload a PNG or SVG logo' : `Maximum ${MAX_LOGOS} logos reached`}
                    >
                        + Add Logo
                    </button>
                    <input
                        ref={this.fileInputRef}
                        type="file"
                        accept=".png,.svg,image/png,image/svg+xml"
                        multiple
                        style={{ display: 'none' }}
                        onChange={this.handleFileChange}
                    />
                </div>

                {/* ── Logo list ── */}
                {logos.length === 0 && (
                    <p style={styles.hint}>
                        Upload up to {MAX_LOGOS} PNG or SVG logos.<br />
                        Use the sliders to position and size each one.
                    </p>
                )}

                {logos.map((logo) => (
                    <div key={logo.id} style={styles.logoCard}>

                        {/* Header: thumb + name + remove */}
                        <div style={styles.logoCardHeader}>
                            <img src={logo.dataUrl} alt={logo.name} style={styles.logoThumb} />
                            <span style={styles.logoName} title={logo.name}>{logo.name}</span>
                            <button
                                style={styles.removeBtn}
                                onClick={() => onRemove(logo.id)}
                                title="Remove logo"
                            >
                                ✕
                            </button>
                        </div>

                        <div style={styles.divider} />

                        {/* X position */}
                        <div style={styles.controlRow}>
                            <div style={styles.controlLabel}>
                                <span>X Position</span>
                                <span style={styles.controlValue}>{Math.round(logo.x)}</span>
                            </div>
                            <input
                                type="range" min="0" max="1650" step="1"
                                value={logo.x}
                                style={styles.slider}
                                onChange={(e) => onUpdate(logo.id, { x: Number(e.target.value) })}
                            />
                        </div>

                        {/* Y position */}
                        <div style={styles.controlRow}>
                            <div style={styles.controlLabel}>
                                <span>Y Position</span>
                                <span style={styles.controlValue}>{Math.round(logo.y)}</span>
                            </div>
                            <input
                                type="range" min="0" max="1550" step="1"
                                value={logo.y}
                                style={styles.slider}
                                onChange={(e) => onUpdate(logo.id, { y: Number(e.target.value) })}
                            />
                        </div>

                        {/* Scale */}
                        <div style={styles.controlRow}>
                            <div style={styles.controlLabel}>
                                <span>Scale</span>
                                <span style={styles.controlValue}>{logo.scale.toFixed(2)}×</span>
                            </div>
                            <input
                                type="range" min="0.05" max="4" step="0.01"
                                value={logo.scale}
                                style={styles.slider}
                                onChange={(e) => onUpdate(logo.id, { scale: Number(e.target.value) })}
                            />
                        </div>

                        {/* Rotation */}
                        <div style={styles.controlRow}>
                            <div style={styles.controlLabel}>
                                <span>Rotate</span>
                                <span style={styles.controlValue}>{Math.round(logo.rotate)}°</span>
                            </div>
                            <input
                                type="range" min="-180" max="180" step="1"
                                value={logo.rotate}
                                style={styles.slider}
                                onChange={(e) => onUpdate(logo.id, { rotate: Number(e.target.value) })}
                            />
                        </div>

                    </div>
                ))}

            </div>
        );
    }
}

export default LogoPlacer;
